// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Bootstrap: resolve the room (from the link hash or freshly minted), join the
 * mesh, and wire the transport to the UI. Sending and receiving messages runs on
 * the main thread — each message is a small AES-GCM blob, so a worker would only
 * add latency; the transport itself is already off-thread inside trystero.
 */
import './styles/main.css';
import QRCode from 'qrcode';
import { AppUI } from './ui.ts';
import { initModals, toast } from './modals.ts';
import { initGlossary } from './glossary.ts';
import { emit } from './eventlog.ts';
import { readInvite, writeRoomHash, currentInviteUrl } from './router.ts';
import { generateRoomId, generatePassphrase, normalizePass, normalizeName } from './room.ts';
import { deriveMessageKey, deriveSasWords, encryptBytes, decryptBytes } from './crypto.ts';
import { encodeEnvelope, decodeEnvelope, packFrame, unpackFrame } from './envelope.ts';
import { makeMsgId, randomGuestName, imageFilename } from './text.ts';
import { hueForId } from './color.ts';
import { fmtClock } from './format.ts';
import { ChatSession, type Hello } from './session.ts';
import type { ChatMessage, Member, Status } from './types.ts';

const LS_NAME = 'chatwell.name';
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const TYPING_STOP_MS = 2500; // stop sending "typing" after this idle
const TYPING_HEARTBEAT_MS = 3000; // re-send "typing" at least this often while active
const TYPING_EXPIRE_MS = 6000; // drop a stale remote typing signal after this

let room = '';
let pass = '';
let displayName = '';

let session: ChatSession | null = null;
let msgKey: CryptoKey | null = null;
let qrRendered = false;

const members = new Map<string, Member>(); // id → member (includes self)
const messages: ChatMessage[] = [];

// Typing state
let typingActive = false;
let lastTypingSentAt = 0;
let typingStopTimer: ReturnType<typeof setTimeout> | null = null;
const remoteTyping = new Map<string, ReturnType<typeof setTimeout>>(); // peerId → expiry timer

const store = safeStorage();

const ui = new AppUI(document.getElementById('app') as HTMLElement, {
  onSend: sendText,
  onInput: handleInput,
  onPickImage: (files) => void sendImageFile(files[0]),
  onCopyLink: copyLink,
  onShareLink: shareLink,
  onToggleQr: toggleQr,
  onNameChange: changeName,
  onExport: exportTranscript,
  onClearMessages: clearMessages,
  onLeave: leaveRoom,
  onSaveImage: (id) => void saveImage(id),
  onShareImage: (id) => void shareImage(id),
  onCopyText: (id) => void copyText(id),
});

init();

function init(): void {
  initModals();
  initGlossary();

  displayName = store.getItem(LS_NAME) || randomGuestName();
  ui.setName(displayName);

  const invite = readInvite();
  if (invite && invite.pass) {
    room = invite.room;
    pass = normalizePass(invite.pass);
  } else {
    if (invite && !invite.pass) toast('That link was missing its passphrase — opened a fresh room.', 'err');
    room = generateRoomId();
    pass = generatePassphrase();
    writeRoomHash(room, pass, true);
  }

  window.addEventListener('pagehide', () => session?.leave());
  window.addEventListener('beforeunload', () => session?.leave());

  void connect();
  installTestHook();
}

async function connect(): Promise<void> {
  ui.setStatus('connecting', 1);
  const url = currentInviteUrl(room, pass, true);
  ui.setInviteLink(url);
  void renderSas();

  // Seed the member list with ourselves.
  members.clear();
  members.set(selfIdOrTemp(), { id: selfIdOrTemp(), name: displayName, hue: hueForId(selfIdOrTemp()) });

  try {
    msgKey = await deriveMessageKey(pass, room);
  } catch (e) {
    emit('bad', `Key derivation failed: ${(e as Error).message}`);
    toast('Could not set up encryption for this room.', 'err');
    ui.setStatus('error', 1);
    return;
  }

  session = new ChatSession(room, pass, {
    onPeerJoin: handlePeerJoin,
    onPeerLeave: handlePeerLeave,
    onHello: handleHello,
    onMessage: handleMessage,
    onTyping: handleRemoteTyping,
    onError: (m) => {
      emit('bad', m);
      toast(m, 'err');
      ui.setStatus('error', members.size);
    },
  });
  const ok = session.join();
  if (ok) {
    // Re-key the self member under the real transport id now that we've joined.
    reseedSelf();
    ui.setStatus('waiting', members.size);
    renderMembers();
    emit('info', 'Room open. Share the invite link to bring people in.');
  }
}

/** trystero's selfId is stable per tab; use it once the session exists. */
function selfIdOrTemp(): string {
  return session?.selfId ?? 'self';
}
function reseedSelf(): void {
  const real = selfIdOrTemp();
  if (!members.has(real)) {
    members.delete('self');
    members.set(real, { id: real, name: displayName, hue: hueForId(real) });
  }
}

// ── presence ────────────────────────────────────────────────────────────────
function handlePeerJoin(peerId: string): void {
  ensureMember(peerId);
  // Tell the newcomer who we are.
  session?.sendHello({ name: displayName }, peerId);
  ui.setStatus('live', members.size);
  renderMembers();
  emit('good', 'Someone joined the room.');
}

function handlePeerLeave(peerId: string): void {
  const m = members.get(peerId);
  members.delete(peerId);
  clearRemoteTyping(peerId);
  renderMembers();
  ui.setStatus(members.size > 1 ? 'live' : 'waiting', members.size);
  emit('warn', `${m?.name || 'Someone'} left the room.`);
}

function handleHello(peerId: string, hello: Hello): void {
  const name = normalizeName(hello?.name || '') || 'Guest';
  const m = ensureMember(peerId);
  m.name = name;
  renderMembers();
}

function ensureMember(peerId: string): Member {
  let m = members.get(peerId);
  if (!m) {
    m = { id: peerId, name: 'Guest', hue: hueForId(peerId) };
    members.set(peerId, m);
  }
  return m;
}

function renderMembers(): void {
  const list = [...members.values()].sort((a, b) => (a.id === selfIdOrTemp() ? -1 : b.id === selfIdOrTemp() ? 1 : a.name.localeCompare(b.name)));
  ui.renderMembers(list, selfIdOrTemp());
}

// ── receiving ───────────────────────────────────────────────────────────────
async function handleMessage(peerId: string, frame: Uint8Array): Promise<void> {
  if (!msgKey) return;
  clearRemoteTyping(peerId); // a delivered message ends "typing"
  try {
    const { iv, cipher } = unpackFrame(frame);
    const plain = await decryptBytes(msgKey, iv, cipher);
    const { header, payload } = decodeEnvelope(plain);
    const at = typeof header.at === 'number' ? header.at : Date.now();
    const member = ensureMember(peerId);
    const authorName = member.name !== 'Guest' ? member.name : normalizeName(header.from || '') || member.name;

    let msg: ChatMessage;
    if (header.kind === 'image') {
      const bytes = payload.slice();
      const mime = header.mime || 'image/jpeg';
      const blob = new Blob([bytes as BlobPart], { type: mime });
      msg = {
        id: header.id, authorId: peerId, authorName, self: false, kind: 'image', at,
        imageName: header.name, mime, size: bytes.length, bytes, blobUrl: URL.createObjectURL(blob),
      };
      emit('good', `${authorName} sent an image.`);
    } else {
      msg = { id: header.id, authorId: peerId, authorName, self: false, kind: 'text', at, text: header.text ?? '' };
      emit('good', `Message from ${authorName}.`);
    }
    messages.push(msg);
    ui.addMessage(msg, member);
  } catch (e) {
    emit('bad', `Could not open a received message: ${(e as Error).message}`);
  }
}

// ── sending ─────────────────────────────────────────────────────────────────
function sendText(text: string): void {
  if (!msgKey || !session) {
    toast('Still connecting — try again in a moment.', 'err');
    return;
  }
  stopTypingNow();
  const id = makeMsgId();
  const at = Date.now();
  const self = ensureMember(selfIdOrTemp());
  const msg: ChatMessage = { id, authorId: selfIdOrTemp(), authorName: displayName, self: true, kind: 'text', at, text, state: 'sending' };
  messages.push(msg);
  ui.addMessage(msg, self);
  void (async () => {
    try {
      const env = encodeEnvelope({ id, kind: 'text', at, from: displayName, text });
      await broadcast(env, id);
      setSelfState(id, 'sent');
      emit('xfer', 'Message sent.');
    } catch (e) {
      setSelfState(id, 'error');
      emit('bad', `Send failed: ${(e as Error).message}`);
      toast('Message failed to send.', 'err');
    }
  })();
}

async function sendImageFile(file: File | undefined): Promise<void> {
  if (!file) return;
  if (!msgKey || !session) {
    toast('Still connecting — try again in a moment.', 'err');
    return;
  }
  if (!file.type.startsWith('image/')) {
    toast('Only images can be sent inline.', 'err');
    return;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    toast(`Image is too large (max ${Math.round(MAX_IMAGE_BYTES / 1024 / 1024)} MB). Use Dropwell for big files.`, 'err');
    return;
  }
  stopTypingNow();
  const bytes = new Uint8Array(await file.arrayBuffer());
  const id = makeMsgId();
  const at = Date.now();
  const mime = file.type;
  const name = file.name || `image.${extFor(mime)}`;
  const self = ensureMember(selfIdOrTemp());
  const blob = new Blob([bytes as BlobPart], { type: mime });
  const msg: ChatMessage = {
    id, authorId: selfIdOrTemp(), authorName: displayName, self: true, kind: 'image', at,
    imageName: name, mime, size: bytes.length, bytes, blobUrl: URL.createObjectURL(blob), state: 'sending',
  };
  messages.push(msg);
  ui.addMessage(msg, self);
  try {
    const env = encodeEnvelope({ id, kind: 'image', at, from: displayName, name, mime }, bytes);
    await broadcast(env, id);
    setSelfState(id, 'sent');
    emit('xfer', `Image sent (${Math.round(bytes.length / 1024)} KB).`);
  } catch (e) {
    setSelfState(id, 'error');
    emit('bad', `Image send failed: ${(e as Error).message}`);
    toast('Image failed to send.', 'err');
  }
}

async function broadcast(env: Uint8Array, id: string): Promise<void> {
  if (!msgKey || !session) throw new Error('not connected');
  const { iv, cipher } = await encryptBytes(msgKey, env);
  const frame = packFrame(iv, cipher);
  await session.broadcastMessage(frame, id);
}

function setSelfState(id: string, state: ChatMessage['state']): void {
  const m = messages.find((x) => x.id === id);
  if (m) m.state = state;
  ui.updateMessageState(id, state);
}

// ── typing ──────────────────────────────────────────────────────────────────
function handleInput(): void {
  if (!session) return;
  // Send a fresh typing signal on the first keystroke, and re-send as a
  // heartbeat if it has been a while — otherwise a peer's TYPING_EXPIRE_MS
  // timer would clear the indicator while someone is still typing a long
  // message. The heartbeat interval is comfortably under TYPING_EXPIRE_MS.
  const now = Date.now();
  if (!typingActive || now - lastTypingSentAt > TYPING_HEARTBEAT_MS) {
    typingActive = true;
    lastTypingSentAt = now;
    session.sendTyping(true);
  }
  if (typingStopTimer) clearTimeout(typingStopTimer);
  typingStopTimer = setTimeout(stopTypingNow, TYPING_STOP_MS);
}

function stopTypingNow(): void {
  if (typingStopTimer) {
    clearTimeout(typingStopTimer);
    typingStopTimer = null;
  }
  if (typingActive) {
    typingActive = false;
    session?.sendTyping(false);
  }
}

function handleRemoteTyping(peerId: string, on: boolean): void {
  if (on) {
    ensureMember(peerId);
    const prev = remoteTyping.get(peerId);
    if (prev) clearTimeout(prev);
    remoteTyping.set(peerId, setTimeout(() => clearRemoteTyping(peerId), TYPING_EXPIRE_MS));
  } else {
    clearRemoteTyping(peerId);
    return;
  }
  renderTyping();
}

function clearRemoteTyping(peerId: string): void {
  const t = remoteTyping.get(peerId);
  if (t) clearTimeout(t);
  if (remoteTyping.delete(peerId)) renderTyping();
}

function renderTyping(): void {
  const names = [...remoteTyping.keys()].map((id) => members.get(id)?.name || 'Someone');
  ui.setTyping(names);
}

// ── name ────────────────────────────────────────────────────────────────────
function changeName(raw: string): void {
  const name = normalizeName(raw) || randomGuestName();
  if (name === displayName) return;
  displayName = name;
  store.setItem(LS_NAME, name);
  ui.setName(name);
  const self = members.get(selfIdOrTemp());
  if (self) self.name = name;
  renderMembers();
  session?.sendHello({ name });
}

// ── message actions ─────────────────────────────────────────────────────────
async function copyText(id: string): Promise<void> {
  const m = messages.find((x) => x.id === id);
  if (!m || m.kind !== 'text' || !m.text) return;
  try {
    await navigator.clipboard.writeText(m.text);
    toast('Copied.', 'ok');
  } catch {
    toast('Could not copy — select the text manually.', 'err');
  }
}

async function saveImage(id: string): Promise<void> {
  const m = messages.find((x) => x.id === id);
  if (!m || m.kind !== 'image' || !m.bytes) return;
  const name = imageFilename(m.imageName, m.mime, m.at);
  const blob = new Blob([m.bytes as BlobPart], { type: m.mime || 'image/jpeg' });
  const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({ suggestedName: name });
      const w = await (handle as FileSystemFileHandle).createWritable();
      await w.write(blob);
      await w.close();
      toast('Image saved.', 'ok');
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
    }
  }
  anchorDownload(blob, name);
  toast('Image saved.', 'ok');
}

async function shareImage(id: string): Promise<void> {
  const m = messages.find((x) => x.id === id);
  if (!m || m.kind !== 'image' || !m.bytes) return;
  const name = imageFilename(m.imageName, m.mime, m.at);
  const file = new File([m.bytes as BlobPart], name, { type: m.mime || 'image/jpeg' });
  const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: name });
    } catch {
      /* cancelled */
    }
  } else {
    void saveImage(id);
  }
}

// ── transcript export (the takeaway artefact) ────────────────────────────────
function buildTranscript(): string {
  const lines: string[] = [];
  lines.push('Chatwell transcript');
  lines.push(`Room: ${room}`);
  lines.push(`Exported: ${new Date().toString()}`);
  lines.push(`Participants: ${[...members.values()].map((m) => m.name).join(', ')}`);
  lines.push('—'.repeat(20));
  for (const m of [...messages].sort((a, b) => a.at - b.at)) {
    const t = fmtClock(new Date(m.at));
    if (m.kind === 'image') {
      lines.push(`[${t}] ${m.authorName}: [image: ${imageFilename(m.imageName, m.mime, m.at)}]`);
    } else {
      lines.push(`[${t}] ${m.authorName}: ${m.text ?? ''}`);
    }
  }
  lines.push('');
  lines.push('Ends. Chatwell keeps nothing — this file is the only copy.');
  return lines.join('\n');
}

async function exportTranscript(): Promise<void> {
  if (messages.length === 0) {
    toast('No messages to export yet.', 'err');
    return;
  }
  const text = buildTranscript();
  const blob = new Blob([text], { type: 'text/plain' });
  const name = `chatwell-${room}.txt`;
  const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({
        suggestedName: name,
        types: [{ description: 'Text', accept: { 'text/plain': ['.txt'] } }],
      });
      const w = await (handle as FileSystemFileHandle).createWritable();
      await w.write(blob);
      await w.close();
      emit('info', 'Transcript exported to your device.');
      toast('Transcript saved.', 'ok');
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
    }
  }
  anchorDownload(blob, name);
  emit('info', 'Transcript exported to your device.');
  toast('Transcript saved.', 'ok');
}

// ── room controls ────────────────────────────────────────────────────────────
function clearMessages(): void {
  for (const m of messages) if (m.blobUrl) URL.revokeObjectURL(m.blobUrl);
  messages.length = 0;
  ui.clearMessages();
  emit('info', 'Messages cleared on this device.');
}

function leaveRoom(): void {
  session?.leave();
  session = null;
  stopTypingNow();
  for (const t of remoteTyping.values()) clearTimeout(t);
  remoteTyping.clear();
  ui.setTyping([]);
  clearMessages();
  emit('warn', 'You left the room. Opened a fresh one.');
  // Mint a brand-new ephemeral room so the link on screen is usable again.
  room = generateRoomId();
  pass = generatePassphrase();
  qrRendered = false;
  writeRoomHash(room, pass, true);
  void connect();
}

// ── invite link + QR ─────────────────────────────────────────────────────────
async function copyLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(currentInviteUrl(room, pass, true));
    toast('Invite link copied.', 'ok');
  } catch {
    toast('Could not copy — select the link and copy manually.', 'err');
  }
}

async function shareLink(): Promise<void> {
  const url = currentInviteUrl(room, pass, true);
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Chatwell', text: 'Join my private Chatwell room', url });
    } catch {
      /* cancelled */
    }
  } else {
    void copyLink();
  }
}

function toggleQr(): void {
  if (!qrRendered) {
    void renderQr(currentInviteUrl(room, pass, true));
    qrRendered = true;
  }
  ui.toggleQr();
}

async function renderQr(url: string): Promise<void> {
  try {
    const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 320, color: { dark: '#06201d', light: '#d9f6f0' } });
    ui.setQr(dataUrl);
  } catch {
    /* QR is a nicety */
  }
}

async function renderSas(): Promise<void> {
  try {
    ui.setSas(await deriveSasWords(pass, room));
  } catch {
    /* ignore */
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────
function anchorDownload(blob: Blob, name: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

function extFor(mime: string): string {
  return mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : mime === 'image/gif' ? 'gif' : 'jpg';
}

function safeStorage(): { getItem: (k: string) => string | null; setItem: (k: string, v: string) => void } {
  try {
    const k = '__chatwell_probe';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return { getItem: (key) => mem.get(key) ?? null, setItem: (key, v) => void mem.set(key, v) };
  }
}

// ── automated two-tab test hook ─────────────────────────────────────────────
// The preview browser can't test a real multi-device room, but two tabs joining
// the same room over the real relays proves discovery + mesh connect +
// encrypt/decrypt + presence + typing end-to-end. Drives REAL transport + crypto.
function installTestHook(): void {
  (window as unknown as { __chatwellTest?: unknown }).__chatwellTest = {
    room: () => room,
    pass: () => pass,
    selfId: () => selfIdOrTemp(),
    here: () => members.size,
    memberNames: () => [...members.values()].map((m) => m.name),
    status: (): Status | 'connected' => (members.size > 1 ? 'connected' : 'waiting'),
    sas: () => deriveSasWords(pass, room),
    send: (t: string) => sendText(t),
    setName: (n: string) => changeName(n),
    typing: (on: boolean) => (on ? handleInput() : stopTypingNow()),
    transcript: () => buildTranscript(),
    messages: () =>
      [...messages]
        .sort((a, b) => a.at - b.at)
        .map((m) => ({ id: m.id, self: m.self, author: m.authorName, kind: m.kind, text: m.text, state: m.state })),
  };
}
